/** @jsxImportSource @emotion/react */
import styled from '@emotion/styled/macro'
import { useEffect, useMemo, useState } from 'react'
import { FormControlLabel, Switch } from '@material-ui/core'
import { withStyles } from '@material-ui/core/styles'

import { PRIMARY_COLOR, TEXTFIELD_LABEL_COLOR } from 'lib/constants'
import type { Organization } from 'lib/types'
import type { License } from 'lib/types/license'
import { createLicense, deleteLicense } from 'models/api/licenses'
import { useGlobal } from 'contexts/GlobalProvider'

const Section = styled.div({
  width: '100%',
  marginTop: 10,
  marginBottom: 10,
})

const Row = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
})

const Title = styled.div<{ color?: string }>(props => ({
  color: props.color ?? TEXTFIELD_LABEL_COLOR,
  fontFamily: 'Montserrat',
  fontSize: '1.4rem',
  fontStyle: 'normal',
  fontWeight: 'bold',
  lineHeight: '1.5rem',
  letterSpacing: '0.1rem',
  textAlign: 'left',
  marginBottom: 0,
}))

const SwitchLabel = styled.div({
  fontSize: '1.0rem',
})

/**
 * Match GeofenceSettings toggle styling
 */
const ToggleSwitch = withStyles({
  switchBase: {
    color: 'white',
    '&$checked': {
      color: 'rgba(48, 188, 176, 1)',
    },
    '&$checked + $track': {
      backgroundColor: 'rgba(48, 188, 176, 0.33)',
    },
  },
  checked: {},
  track: {},
})(Switch)

type Props = {
  org: Organization | null
  orgId: string
  userId: string
  title?: string
  titleColor?: string
}

export default function VoiceLicenseToggleSection({ org, orgId, userId, title = 'Voice license', titleColor }: Props) {
  // If your GlobalProvider has proper typing, replace "as any" with your GlobalContextType
  const { licenses, licensesLoading, licensesError, reloadLicenses } = useGlobal() as any

  const orgLicenses = useMemo<License[]>(() => {
    return (licenses || []).filter((l: License) => l?.orgId === orgId && l?.type === 'standalone_voice')
  }, [licenses, orgId])

  const existing = useMemo<License | undefined>(() => {
    return orgLicenses.find(l => String(l.userId) === String(userId))
  }, [orgLicenses, userId])

  const [enabled, setEnabled] = useState<boolean>(Boolean(existing))
  const [saving, setSaving] = useState(false)
  const [errorMsg, setErrorMsg] = useState<string>('')
  const [successMsg, setSuccessMsg] = useState<string | null>(null)

  // Keep toggle in sync with global licenses
  useEffect(() => {
    setEnabled(Boolean(existing))
  }, [existing?.id])

  // Placeholder "availability" check (refine later)
  const maxSeats = typeof (org as any)?.zelloUserMax === 'number' ? Number((org as any).zelloUserMax) : 0
  const assignedSeats = orgLicenses.length
  const noSeatsAvailable = maxSeats > 0 ? assignedSeats >= maxSeats : false

  const disableEnable = (!enabled && noSeatsAvailable) || licensesLoading

  // ✅ Show an error text when there are no seats and the user does not currently have one.
  // (If they already have a seat, don't show the error.)
  const LIMIT_ERROR = 'Disable another user or reach out to CSM to increase the voice license limit'
  useEffect(() => {
    if (!enabled && noSeatsAvailable) {
      setErrorMsg(LIMIT_ERROR)
    } else if (errorMsg === LIMIT_ERROR) {
      // Only clear *this specific* auto-generated error so we don't wipe other errors.
      setErrorMsg('')
    }
  }, [enabled, noSeatsAvailable]) // intentionally not including errorMsg to avoid extra

  const handleToggle = async (checked: boolean) => {
    setSuccessMsg(null)
    setErrorMsg('')

    if (saving) return

    // If turning ON but we already have one, keep consistent
    if (checked && existing) {
      setEnabled(true)
      return
    }

    // If turning OFF but none exists, keep consistent
    if (!checked && !existing) {
      setEnabled(false)
      return
    }

    if (checked && noSeatsAvailable) {
      setErrorMsg('No voice licenses available.')
      return
    }

    setSaving(true)
    try {
      if (checked) {
        // ✅ FIX: your api helper expects (orgId, userId)
        await createLicense(orgId, userId)
        setSuccessMsg('Voice license assigned.')
      } else {
        // If your deleteLicense is (id, orgId), keep this:
        await deleteLicense(existing!.id, orgId)

        // If your deleteLicense is ONLY (id), use this instead:
        // await deleteLicense(existing!.id)

        setSuccessMsg('Voice license removed.')
      }

      if (typeof reloadLicenses === 'function') {
        await reloadLicenses(orgId)
      }

      setEnabled(checked)
    } catch (err: any) {
      setErrorMsg(err?.message || 'Unable to update voice license.')
      setEnabled(Boolean(existing))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Section>
      <Row>
        <Title color={titleColor}>{title}</Title>

        <FormControlLabel
          style={{ marginRight: 0 }}
          control={
            <ToggleSwitch
              checked={enabled}
              onChange={(_, checked) => handleToggle(checked)}
              name="voice-license"
              disabled={disableEnable}
            />
          }
          label={<SwitchLabel>{enabled ? 'Enabled' : 'Disabled'}</SwitchLabel>}
        />
      </Row>

      {/* Messages directly below (geofence style) */}
      {!!licensesError && <div style={{ color: 'red', marginTop: '10px' }}>{String(licensesError)}</div>}
      {!!errorMsg && <div style={{ color: 'red', marginTop: '10px' }}>{errorMsg}</div>}
      {!!successMsg && !errorMsg && <div style={{ color: PRIMARY_COLOR, marginTop: '10px' }}>{successMsg}</div>}
    </Section>
  )
}
